import React, { useCallback, useEffect, useRef, useState } from 'react';
import CommonModal from './CommonModal';
import { callAPI } from '../../utils/apiUtils';
import { cachedCall } from '../../utils/dataCache';
import { apiUrls } from '../../utils/apiUrls';
import { getSessionId } from '../../utils/sessionId';
import { toast } from 'react-toastify';
import { defaultSizeId, stepSizesForProduct } from '../../utils/dealSizes';
import { dealPlaceholderSpec } from '../../utils/dealPrice';
import '../../styles/DealPopupModal.css';

const DealPopupModal = ({ isOpen, onClose, dealId, dealSizeId, onCartChange, orderType, editOrderId, initialSelections, deletedProductId, existingParentCartId, onPendingAddDeal, onPendingSettle, onPendingFail }) => {
  const [dealData,          setDealData]          = useState(null);
  const [loading,           setLoading]           = useState(false);
  const [stepIdx,           setStepIdx]           = useState(0);
  const [activeTier,        setActiveTier]        = useState({});
  const [selections,        setSelections]        = useState({});
  const [recipes,           setRecipes]           = useState({});
  const [recipeLoading,     setRecipeLoading]     = useState({});
  const [submitting,        setSubmitting]        = useState(false);
  const [deletedStepIdxRef, setDeletedStepIdxRef] = useState(-1);
  /* customise sub-panel */
  const [cpOpen,            setCpOpen]            = useState(false);
  const [cpStep,            setCpStep]            = useState(null);
  const [cpProd,            setCpProd]            = useState(null);
  const [stepSubCat,        setStepSubCat]        = useState({});
  const bodyRef = useRef(null);

  const steps       = dealData?.steps    || [];
  const productsMap = dealData?.products || {};
  const currentStep = steps[stepIdx]     || null;
  const isLastStep  = stepIdx === steps.length - 1;
  const sel         = currentStep ? selections[currentStep.id] : null;
  const canAdvance  = !!(sel?.productId && (sel?.sizeId || !(currentStep?.sizes?.length > 0)));

  /* ── helpers ─────────────────────────────────────────────────────── */

  const getActiveTierId = useCallback((step) =>
    activeTier[step.id] || step.tiers?.find(t => t.upgrade === 0)?.id || step.tiers?.[0]?.id,
  [activeTier]);

  const getStepProducts = useCallback((step) => {
    if (!step) return [];
    const activeTierId  = getActiveTierId(step);
    const activeTierObj = step.tiers?.find(t => t.id === activeTierId);
    const mapKey        = activeTierObj?.category || activeTierId;
    let all = productsMap[mapKey] || [];
    // Size-match: hide products whose size list has no overlap with the step's configured sizes
    const stepSizeIds = (step.sizes || []).map(s => s.id);
    if (stepSizeIds.length > 0) {
      all = all.filter(p => p.sizes == null || p.sizes.some(sid => stepSizeIds.includes(sid)));
    }
    // For the base tier only, filter to the products the deal config explicitly listed
    if (step.slotProductIds?.length > 0) {
      if (!activeTierObj || activeTierObj.upgrade === 0) {
        return all.filter(p => step.slotProductIds.includes(p.id));
      }
    }
    return all;
  }, [productsMap, getActiveTierId]);

  /* ── auto-advance ────────────────────────────────────────────────── */

  const tryAutoSelect = useCallback((step) => {
    if (!step) return false;
    const tiers = step.tiers || [];
    if (tiers.length > 1) return false;
    const tid   = tiers[0]?.id;
    // Use getStepProducts so slotProductIds filtering applies (matches backend auto-add logic)
    const prods = getStepProducts(step);
    if (prods.length !== 1) return false;
    const prod  = prods[0];
    // Allow auto-select even for customisable products — user can click to customise if needed
    const sizes  = step.sizes || [];
    if (sizes.length > 1) return false;
    const sizeId = sizes[0]?.id || null;
    setSelections(prev => ({
      ...prev,
      [step.id]: { productId: prod.id, sizeId, tierId: tid, autoSelected: true },
    }));
    return true;
  }, [getStepProducts]);

  /* ── fetch deal data ─────────────────────────────────────────────── */

  useEffect(() => {
    if (!isOpen || !dealId) return;
    setLoading(true);
    setDealData(null);
    setStepIdx(0);
    setSelections({});
    setActiveTier({});
    setRecipes({});
    setStepSubCat({});
    setCpOpen(false);

    cachedCall(`deal_data_${dealId}`, () => callAPI(`${apiUrls.DEAL_DATA}?id=${dealId}`, {}, 'GET', null, {}, {}, false))
      .then(res => {
        const data = res.data || res.data?.data;
        setDealData(data);

        // Pre-select remaining items when popup is reopened after a deal child deletion.
        if (initialSelections?.length > 0 && data?.steps && data?.products) {
          const preSelected = {};

          // Find which step the deleted product belongs to — keep that step empty.
          // Search ALL tiers (not just tiers[0]) because position sort may put upgrade first.
          let deletedStepIdx = -1;
          if (deletedProductId) {
            data.steps.forEach((step, si) => {
              if (deletedStepIdx >= 0) return;
              const found = (step.tiers || []).some(tierObj => {
                const all = data.products[tierObj.category || tierObj.id] || [];
                return all.some(p => String(p.id) === String(deletedProductId));
              });
              if (found) deletedStepIdx = si;
            });
          }

          // Place each remaining product into the first matching step (skip the deleted step).
          // Try base tier (upgrade=0) first, then upgrade tiers, to use the correct tierId.
          initialSelections.forEach(productId => {
            let placed = false;
            data.steps.forEach((step, si) => {
              if (placed || si === deletedStepIdx || preSelected[step.id]) return;
              const tiersToTry = [...(step.tiers || [])].sort((a, b) => a.upgrade - b.upgrade);
              for (const tierObj of tiersToTry) {
                const tierId = tierObj.id;
                const all = data.products[tierObj.category || tierId] || [];
                let prods = all;
                if (step.slotProductIds?.length > 0 && tierObj.upgrade === 0) {
                  prods = all.filter(p => step.slotProductIds.includes(p.id));
                }
                const match = prods.find(p => String(p.id) === String(productId));
                if (match) {
                  const sizeId = step.sizes?.length === 1 ? step.sizes[0].id : null;
                  preSelected[step.id] = { productId: match.id, sizeId, tierId, autoSelected: false };
                  placed = true;
                  break;
                }
              }
            });
          });

          if (Object.keys(preSelected).length > 0) setSelections(preSelected);

          // Open directly on the deleted step (or first empty step if not found).
          const startIdx = deletedStepIdx >= 0
            ? deletedStepIdx
            : data.steps.findIndex(step => !preSelected[step.id]);
          if (startIdx >= 0) setStepIdx(startIdx);
          setDeletedStepIdxRef(deletedStepIdx >= 0 ? deletedStepIdx : startIdx);
        }
      })
      .catch(() => toast.error('Failed to load deal'))
      .finally(() => setLoading(false));
  }, [isOpen, dealId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentStep || !dealData) return;
    const didSelect = tryAutoSelect(currentStep);
    if (!didSelect || stepIdx >= steps.length - 1) return;
    // Auto-advance to next step after a brief pause so the auto-selection is visible
    const t = setTimeout(() => {
      setStepIdx(i => i + 1);
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
    }, 250);
    return () => clearTimeout(t);
  }, [stepIdx, currentStep, dealData]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── product click ───────────────────────────────────────────────── */

  const recipeIsEmpty = (rec) =>
    !rec || (!rec.baseGroups?.length && !rec.included?.length && !rec.addons?.length && !rec.chilli?.length);

  const fetchRecipe = useCallback((prod, stepId) => {
    if (!prod.customisable) return;
    if (recipes[prod.id] !== undefined) return;
    setRecipeLoading(prev => ({ ...prev, [prod.id]: true }));
    cachedCall(`recipe_${prod.id}`, () => callAPI(`${apiUrls.PRODUCT_RECIPE}?id=${prod.id}`, {}, 'GET', null, {}, {}, false))
      .then(res => {
        const recipe = res.data || false;
        setRecipes(prev => ({ ...prev, [prod.id]: recipe }));

        // Auto-select first (included) option for each base group
        if (recipe && recipe.baseGroups?.length > 0 && stepId) {
          const defaults = {};
          recipe.baseGroups.forEach(bg => {
            if (bg.options?.length > 0) {
              const def = bg.options.find(o => !o.price || o.price === 0) || bg.options[0];
              defaults[bg.key] = def.id;
            }
          });
          if (Object.keys(defaults).length > 0) {
            setSelections(prev => ({
              ...prev,
              [stepId]: { ...(prev[stepId] || {}), ...defaults },
            }));
          }
        }

        // Auto-close cp panel when recipe has no customisation content (only size)
        if (recipeIsEmpty(recipe)) {
          setCpOpen(false);
          setCpProd(null);
          setCpStep(null);
        }
      })
      .catch(()  => setRecipes(prev => ({ ...prev, [prod.id]: false })))
      .finally(() => setRecipeLoading(prev => ({ ...prev, [prod.id]: false })));
  }, [recipes]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProductClick = useCallback((step, prod) => {
    const tid   = getActiveTierId(step);
    // Pre-select the included (price=0) size, or the first size if all are paid - among the sizes the product really has
    const defaultSizeIdValue = defaultSizeId(step.sizes, prod);

    setSelections(prev => {
      const prevSel = prev[step.id];
      if (prevSel?.productId === prod.id) {
        // Same product re-clicked — keep size + all customisation, just update tier
        return { ...prev, [step.id]: { ...prevSel, tierId: tid } };
      }
      return { ...prev, [step.id]: { productId: prod.id, sizeId: defaultSizeIdValue || null, tierId: tid, autoSelected: false } };
    });

    if (prod.customisable) {
      // If recipe is already loaded and empty, don't open cp — sizes show inline
      const loadedRecipe = recipes[prod.id];
      if (loadedRecipe !== undefined && recipeIsEmpty(loadedRecipe)) return;

      fetchRecipe(prod, step.id);
      setCpStep(step);
      setCpProd(prod);
      setCpOpen(true);
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
    }
  }, [getActiveTierId, fetchRecipe, recipes]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSizeClick = useCallback((stepId, sizeId) => {
    setSelections(prev => ({ ...prev, [stepId]: { ...prev[stepId], sizeId } }));
  }, []);

  /* ── customise panel close ───────────────────────────────────────── */
  const closeCp = () => {
    setCpOpen(false);
    setCpProd(null);
    setCpStep(null);
  };

  /* ── navigation ──────────────────────────────────────────────────── */

  const handleNext = () => {
    if (!canAdvance || submitting) return;
    if (isLastStep || existingParentCartId) { handleSubmit(); return; }
    setStepIdx(i => i + 1);
    setCpOpen(false);
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  };

  const handleBack = () => {
    if (stepIdx > 0) { setStepIdx(i => i - 1); setCpOpen(false); }
  };

  /* ── submit ──────────────────────────────────────────────────────── */

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    let tempId = null;           // the line shown in the cart ahead of the server, when there is one
    let placeholderHandled = false;
    let createdParentId = null;  // the deal's own cart line, once the server has made it
    try {
      const session_id  = getSessionId();
      const phone_order = (orderType === 'takeaway' || orderType === 'phone') ? 1 : 0;

      // A new deal shows in the cart and the sheet closes at once; the calls carry on in the background.
      // (Re-adding an item to a deal that is already in the cart, or an instant cart that is switched off,
      // or a deal whose price cannot be worked out here: it waits for the server as before.)
      if (!existingParentCartId && onPendingAddDeal) {
        const spec = dealPlaceholderSpec(dealData, selections, recipes);
        tempId = spec ? onPendingAddDeal(spec) : null;
        if (tempId) onClose();
      }

      let parentCartId;
      let stepsToSubmit;

      if (existingParentCartId) {
        // Reopened after a child delete — keep the existing parent, only add the newly selected child
        parentCartId = existingParentCartId;
        // Only submit the step that was empty (the deleted slot)
        stepsToSubmit = deletedStepIdxRef >= 0 ? [steps[deletedStepIdxRef]].filter(Boolean) : steps;
      } else {
        // Normal first-time add — create the parent deal cart item
        const parentPayload = { id: dealId, size_id: dealSizeId, session_id, phone_order, is_deal: 1, selected_customer_id: 1 };
        if (editOrderId) parentPayload.edit_order_id = editOrderId;
        const parentRes = await callAPI(apiUrls.ADD_TO_CART, {}, 'POST', parentPayload, {}, {}, false);
        // HTTP 200 alone isn't enough: add-default-dish-to-cart can return 200 with
        // status:0 (e.g. an EFTPOS tender already in progress), so the body must be checked too.
        if (!parentRes?.data?.status) {
          throw new Error(parentRes?.data?.message || 'Failed to add deal to cart');
        }
        parentCartId = parentRes?.data?.data?.id || null;
        createdParentId = parentCartId;
        stepsToSubmit = steps;
      }

      // Each step's child item only depends on the already-awaited parent, not on each
      // other, so submit them all concurrently instead of one at a time - a large deal
      // (many pizzas/sides) was taking a slow round trip per item.
      const stepRequests = stepsToSubmit
        .map(step => selections[step.id])
        .filter(stepSel => stepSel?.productId)
        .map(async stepSel => {
          const stepRec = recipes[stepSel.productId];
          const baseGroupKeys = (stepRec?.baseGroups || []).map(bg => bg.key);
          const defaultQtys = stepSel.defaultQtys || {};
          const addedQtys   = stepSel.addedQtys   || {};
          const hasAddons   = Object.keys(addedQtys).length > 0;
          const hasDefaultChanged = Object.keys(defaultQtys).length > 0;
          const hasBaseSelection = baseGroupKeys.some(key => stepSel[key]);
          const hasCustom   = hasDefaultChanged || hasAddons || hasBaseSelection || stepSel.chilli;

          if (hasCustom) {
            const rec = recipes[stepSel.productId];
            const payload = new FormData();
            payload.append('id', stepSel.productId);
            payload.append('qty', 1);
            payload.append('session_id', session_id);
            payload.append('phone_order', phone_order);
            payload.append('selected_customer_id', 1);
            if (stepSel.sizeId) payload.append('size', stepSel.sizeId);
            if (parentCartId) payload.append('linked_item_id', parentCartId);
            if (editOrderId) payload.append('edit_order_id', editOrderId);

            // Included ingredients with real quantities (default 1, user may increase or remove to 0)
            const includedIngs = (rec?.included || [])
              .map(ing => ({ id: ing.id, qty: defaultQtys[ing.id] !== undefined ? defaultQtys[ing.id] : 1 }));
            payload.append('default_ingredients_quantity', JSON.stringify(includedIngs));

            // Addon ingredients the user explicitly added, with real quantities
            if (hasAddons) {
              payload.append('add_on_ingredients_quantity', JSON.stringify(
                Object.entries(addedQtys).map(([id, qty]) => ({ id: Number(id), qty }))
              ));
            }

            // Base selections. Legacy products key by literal base1/base2/base3 (posted as
            // individual *_ingredients fields); grouped ("new product config") products key
            // by dynamic bases_<group_id> names instead — posted together as the same
            // JSON-array shape CartService::formatDishAttributes() already reads for
            // bases_ingredients_quantity (mirrors default/add-on ingredients above).
            const basesGrouped = baseGroupKeys.some(key => key.indexOf('bases_') === 0);
            if (basesGrouped) {
              const basesSel = baseGroupKeys
                .filter(key => stepSel[key])
                .map(key => ({ id: stepSel[key], qty: 1, group: key.replace(/^bases_/, '') }));
              if (basesSel.length) payload.append('bases_ingredients_quantity', JSON.stringify(basesSel));
            } else {
              if (stepSel.base1) payload.append('base1_ingredients', stepSel.base1);
              if (stepSel.base2) payload.append('base2_ingredients', stepSel.base2);
              if (stepSel.base3) payload.append('base3_ingredients', stepSel.base3);
            }

            // Chilli / spice level
            if (stepSel.chilli) payload.append('chilli', stepSel.chilli);

            const detailRes = await callAPI(apiUrls.ADD_TO_CART_DETAIL, {}, 'POST', payload, {}, {}, false);
            if (!detailRes?.data?.status) {
              throw new Error(detailRes?.data?.message || 'Failed to add deal to cart');
            }
          } else {
            const childPayload = { id: stepSel.productId, size_id: stepSel.sizeId, session_id, phone_order, selected_customer_id: 1 };
            if (parentCartId) childPayload.linked_item_id = parentCartId;
            if (editOrderId) childPayload.edit_order_id = editOrderId;
            const childRes = await callAPI(apiUrls.ADD_TO_CART, {}, 'POST', childPayload, {}, {}, false);
            if (!childRes?.data?.status) {
              throw new Error(childRes?.data?.message || 'Failed to add deal to cart');
            }
          }
        });

      await Promise.all(stepRequests);

      if (tempId && onPendingSettle) { onPendingSettle(tempId); placeholderHandled = true; }
      toast.success('Deal added to cart');
      onCartChange?.();
      if (!tempId) onClose();
    } catch (error) {
      const reason = error?.message || error?.response?.data?.message;
      if (tempId) {
        // It was shown ahead and the sheet has closed, so leave nothing half-made behind: when the deal's own
        // line was created but one of its items was not, take the whole deal out again.
        let undone = !createdParentId;
        if (createdParentId) {
          try {
            const removal = { id: createdParentId, session_id: getSessionId() };
            if (editOrderId) removal.edit_order_id = editOrderId;
            await callAPI(apiUrls.REMOVE_CART_ITEM, {}, 'POST', removal, {}, {}, false);
            undone = true;
          } catch (removeError) {
            undone = false;
          }
        }
        if (onPendingFail) { onPendingFail(tempId); placeholderHandled = true; }
        toast.error(undone
          ? `Deal was not added: ${reason || 'the server did not accept it'}`
          : 'The deal was only partly added and could not be taken out again - please check the cart');
        onCartChange?.(); // show what the server really has
      } else {
        toast.error(reason || 'Failed to add deal to cart');
      }
    } finally {
      // whatever happened, a line that was shown for this add is never left behind
      if (tempId && !placeholderHandled && onPendingFail) onPendingFail(tempId);
      setSubmitting(false);
    }
  };

  /* ── customise panel render ──────────────────────────────────────── */

  const renderCpPanel = () => {
    if (!cpProd || !cpStep) return null;
    const rec = recipes[cpProd.id];
    const stepSel = selections[cpStep.id] || {};
    const isLoadingRecipe = recipeLoading[cpProd.id];

    const currentSizeId = stepSel.sizeId;
    const sizes = cpStep.sizes || [];
    const pillSizes = stepSizesForProduct(sizes, cpProd); // only the sizes this product has

    // Live upgrade price: tier upgrade + size upgrade + addon prices
    const activeTierId = getActiveTierId(cpStep);
    const tierUpgrade = cpStep.tiers?.find(t => t.id === activeTierId)?.upgrade || 0;
    const sizeUpgrade = sizes.find(s => s.id === currentSizeId)?.price || 0;
    const addonQtys = stepSel.addedQtys || {};
    const addonUpgrade = Object.entries(addonQtys).reduce((sum, [id, qty]) => {
      const ing = rec?.addons?.find(a => String(a.id) === String(id));
      return sum + (ing?.price || 0) * qty;
    }, 0);
    const livePriceTotal = tierUpgrade + sizeUpgrade + addonUpgrade;
    const livePriceLabel = livePriceTotal > 0 ? `+$${livePriceTotal.toFixed(2)}` : '';

    return (
      <div className={`cp-wrap${cpOpen ? ' open' : ''}`}>
        {/* Sticky header */}
        <div className="cp-hd">
          <button className="cp-back-btn" onClick={closeCp}>
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none">
              <path d="M6 1L1 6l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to selection
          </button>
          {livePriceLabel && <span className="cp-live-price">{livePriceLabel}</span>}
        </div>

        {/* Hero image */}
        <div className="cp-hero">
          {cpProd.img
            ? <img src={cpProd.img} alt={cpProd.name} />
            : <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg,#ede5d8,#d8cfc3)' }} />
          }
          <div className="cp-hero-txt">
            <div className="cp-hero-name">{cpProd.name}</div>
            {cpProd.desc && <div className="cp-hero-sub">{cpProd.desc}</div>}
          </div>
        </div>

        {/* Scrollable content */}
        <div className="cp-scroll">
          {/* Size picker */}
          {sizes.length > 0 && (
            <div className="cp-section">
              <div className="cp-sec-hd">
                <span className="cp-sec-title">Size</span>
                {sizes.length > 1 && (
                  <span className="cp-sec-opt" style={{ background: currentSizeId ? 'rgba(46,125,50,0.1)' : 'rgba(200,134,42,0.14)', color: currentSizeId ? '#2E7D32' : '#C8862A' }}>
                    {currentSizeId ? 'Selected' : 'Required'}
                  </span>
                )}
              </div>
              <div className="size-row">
                {pillSizes.map(size => (
                  <button
                    key={size.id}
                    className={`size-pill${currentSizeId === size.id ? ' on' : ''}`}
                    onClick={() => handleSizeClick(cpStep.id, size.id)}
                  >
                    <span className="sn">{size.label}</span>
                    {size.price > 0
                      ? <span className="sp up">+${size.price}</span>
                      : <span className="sp">Included</span>
                    }
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Recipe sections */}
          {isLoadingRecipe ? (
            <div className="cp-skeleton">
              {[1,2,3].map(i => (
                <div key={i} className="skel-acc">
                  <div className="skel-line skel-lbl" />
                  <div className="skel-line skel-val" />
                </div>
              ))}
            </div>
          ) : rec ? (
            <div className="cp-acc-list">
              {/* Spice level */}
              {rec.chilli?.length > 0 && (
                <div style={{ borderTop: '1px solid var(--border)', padding: '14px 0 0' }}>
                  <div className="cp-sec-hd" style={{ marginBottom: 10 }}>
                    <span className="cp-sec-title">Spice Level</span>
                  </div>
                  <div className="size-row" style={{ paddingTop: 0 }}>
                    {rec.chilli.map(c => (
                      <button
                        key={c.value}
                        className={`size-pill${stepSel.chilli === c.value ? ' on' : ''}`}
                        onClick={() => setSelections(prev => ({
                          ...prev,
                          [cpStep.id]: { ...prev[cpStep.id], chilli: c.value },
                        }))}
                      >
                        <span className="sn">{c.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {rec.baseGroups?.map(bg => (
                <CpAccordion key={bg.key} title={bg.label} summary="Standard">
                  <div className="acc-pad">
                    {bg.options.map(opt => {
                      const isOn = stepSel[bg.key] === opt.id;
                      return (
                        <div
                          key={opt.id}
                          className="radio-row"
                          onClick={() => setSelections(prev => ({
                            ...prev,
                            [cpStep.id]: { ...prev[cpStep.id], [bg.key]: opt.id },
                          }))}
                        >
                          <div className={`radio-mark${isOn ? ' on' : ''}`} />
                          <span className="radio-label">{opt.name}</span>
                          {opt.price > 0 && <span className="radio-price">+${opt.price}</span>}
                        </div>
                      );
                    })}
                  </div>
                </CpAccordion>
              ))}

              {/* Default ("included") ingredients — grouped (real per-group labels, e.g.
                  "Cheese"/"Pasta") when the product's new_product_config is on and has real
                  group data, otherwise the legacy single flat block below. */}
              {rec.isGroupedConfig && rec.includedGroups?.length > 0
                ? rec.includedGroups.map(grp => (
                    <CpAccordion key={grp.key} title={grp.label} summary="All included">
                      <div className="acc-pad">
                        {grp.options.map(ing => {
                          const defQtys = stepSel.defaultQtys || {};
                          const qty = defQtys[ing.id] !== undefined ? defQtys[ing.id] : 1;
                          return (
                            <div key={ing.id} className={`ing-row${qty === 0 ? ' removed' : ''}`}>
                              <span className="ing-label">
                                <span className="ing-name">{ing.name}</span>
                                {ing.price > 0 && <span className="ing-price"> +${ing.price}</span>}
                              </span>
                              <div className="ing-stepper">
                                <button
                                  onClick={() => setSelections(prev => {
                                    const prevQ = prev[cpStep.id]?.defaultQtys || {};
                                    const cur = prevQ[ing.id] !== undefined ? prevQ[ing.id] : 1;
                                    return { ...prev, [cpStep.id]: { ...prev[cpStep.id], defaultQtys: { ...prevQ, [ing.id]: Math.max(0, cur - 1) } } };
                                  })}
                                  disabled={qty === 0}
                                >−</button>
                                <span className="v">{qty}</span>
                                <button
                                  onClick={() => setSelections(prev => {
                                    const prevQ = prev[cpStep.id]?.defaultQtys || {};
                                    const cur = prevQ[ing.id] !== undefined ? prevQ[ing.id] : 1;
                                    return { ...prev, [cpStep.id]: { ...prev[cpStep.id], defaultQtys: { ...prevQ, [ing.id]: cur + 1 } } };
                                  })}
                                >+</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CpAccordion>
                  ))
                : rec.included?.length > 0 && (
                <CpAccordion title={rec.includedLabel} summary="All included">
                  <div className="acc-pad">
                    {rec.included.map(ing => {
                      const defQtys = stepSel.defaultQtys || {};
                      const qty = defQtys[ing.id] !== undefined ? defQtys[ing.id] : 1;
                      return (
                        <div key={ing.id} className={`ing-row${qty === 0 ? ' removed' : ''}`}>
                          <span className="ing-label">
                            <span className="ing-name">{ing.name}</span>
                            {ing.price > 0 && <span className="ing-price"> +${ing.price}</span>}
                          </span>
                          <div className="ing-stepper">
                            <button
                              onClick={() => setSelections(prev => {
                                const prevQ = prev[cpStep.id]?.defaultQtys || {};
                                const cur = prevQ[ing.id] !== undefined ? prevQ[ing.id] : 1;
                                return { ...prev, [cpStep.id]: { ...prev[cpStep.id], defaultQtys: { ...prevQ, [ing.id]: Math.max(0, cur - 1) } } };
                              })}
                              disabled={qty === 0}
                            >−</button>
                            <span className="v">{qty}</span>
                            <button
                              onClick={() => setSelections(prev => {
                                const prevQ = prev[cpStep.id]?.defaultQtys || {};
                                const cur = prevQ[ing.id] !== undefined ? prevQ[ing.id] : 1;
                                return { ...prev, [cpStep.id]: { ...prev[cpStep.id], defaultQtys: { ...prevQ, [ing.id]: cur + 1 } } };
                              })}
                            >+</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CpAccordion>
              )}

              {/* Add-on ingredients — same grouped/flat branch as above. */}
              {rec.isGroupedConfig && rec.addonGroups?.length > 0
                ? rec.addonGroups.map(grp => (
                    <CpAccordion key={grp.key} title={grp.label} summary="None added">
                      <div className="acc-pad">
                        {grp.options.map(ing => {
                          const qty = (stepSel.addedQtys || {})[ing.id] || 0;
                          return (
                            <div key={ing.id} className="ing-row">
                              <span className="ing-label">
                                <span className="ing-name">{ing.name}</span>
                                {ing.price > 0 && <span className="ing-price"> +${ing.price}</span>}
                              </span>
                              <div className="ing-stepper">
                                <button
                                  onClick={() => setSelections(prev => {
                                    const prevQtys = prev[cpStep.id]?.addedQtys || {};
                                    const newQty = Math.max(0, (prevQtys[ing.id] || 0) - 1);
                                    const newQtys = { ...prevQtys };
                                    if (newQty === 0) delete newQtys[ing.id]; else newQtys[ing.id] = newQty;
                                    return { ...prev, [cpStep.id]: { ...prev[cpStep.id], addedQtys: newQtys } };
                                  })}
                                  disabled={qty === 0}
                                >−</button>
                                <span className="v">{qty}</span>
                                <button
                                  onClick={() => setSelections(prev => {
                                    const prevQtys = prev[cpStep.id]?.addedQtys || {};
                                    return { ...prev, [cpStep.id]: { ...prev[cpStep.id], addedQtys: { ...prevQtys, [ing.id]: (prevQtys[ing.id] || 0) + 1 } } };
                                  })}
                                >+</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CpAccordion>
                  ))
                : rec.addons?.length > 0 && (
                <CpAccordion title={rec.addonLabel} summary="None added">
                  <div className="acc-pad">
                    {rec.addons.map(ing => {
                      const qty = (stepSel.addedQtys || {})[ing.id] || 0;
                      return (
                        <div key={ing.id} className="ing-row">
                          <span className="ing-label">
                            <span className="ing-name">{ing.name}</span>
                            {ing.price > 0 && <span className="ing-price"> +${ing.price}</span>}
                          </span>
                          <div className="ing-stepper">
                            <button
                              onClick={() => setSelections(prev => {
                                const prevQtys = prev[cpStep.id]?.addedQtys || {};
                                const newQty = Math.max(0, (prevQtys[ing.id] || 0) - 1);
                                const newQtys = { ...prevQtys };
                                if (newQty === 0) delete newQtys[ing.id]; else newQtys[ing.id] = newQty;
                                return { ...prev, [cpStep.id]: { ...prev[cpStep.id], addedQtys: newQtys } };
                              })}
                              disabled={qty === 0}
                            >−</button>
                            <span className="v">{qty}</span>
                            <button
                              onClick={() => setSelections(prev => {
                                const prevQtys = prev[cpStep.id]?.addedQtys || {};
                                return { ...prev, [cpStep.id]: { ...prev[cpStep.id], addedQtys: { ...prevQtys, [ing.id]: (prevQtys[ing.id] || 0) + 1 } } };
                              })}
                            >+</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CpAccordion>
              )}
            </div>
          ) : null}
        </div>

        {/* Done button */}
        <div className="cp-done-bar">
          <button className="cp-done-btn" onClick={closeCp}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8l3.5 3.5 6.5-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Done — save customisation
          </button>
        </div>
      </div>
    );
  };

  /* ── footer label ────────────────────────────────────────────────── */

  const footerLabel = () => {
    if (!sel?.productId) {
      const cat = currentStep?.label || 'item';
      return `Select ${/^[aeiou]/i.test(cat) ? 'an' : 'a'} ${cat.toLowerCase()} to continue`;
    }
    if (currentStep?.sizes?.length > 0 && !sel?.sizeId) return 'Select a size to continue';
    if (existingParentCartId) return 'Complete Deal';
    if (isLastStep) return 'Add to Cart';
    return `Next: ${steps[stepIdx + 1]?.label || 'Next'}`;
  };

  const nextCls = () => {
    const base = 'ft-next';
    if (!canAdvance) return `${base} off`;
    if (isLastStep || existingParentCartId) return `${base} confirm`;
    return `${base} ready`;
  };

  /* ── JSX ─────────────────────────────────────────────────────────── */

  return (
    <CommonModal isOpen={isOpen} onClose={onClose} elId="dealPopupModal" closeOnEscape={!submitting} closeOnBackdropClick={!submitting}>
      <div className="deal-modal-root">

        {/* Drag handle */}
        <div className="deal-drag-handle" />

        {/* Sheet header */}
        <div className="deal-sheet-hd">
          <div className="deal-sheet-hd-text">
            <div className="deal-sheet-name">{dealData?.label || 'Configure Deal'}</div>
            {currentStep && steps.length > 0 && (
              <div className="deal-sheet-sub">Step {stepIdx + 1} of {steps.length}</div>
            )}
          </div>
          <button className="deal-sheet-close" onClick={onClose} disabled={submitting} aria-label="Close">✕</button>
        </div>

        {/* Progress pills */}
        {steps.length > 1 && (
          <div className="progress-row">
            {steps.map((s, i) => {
              const isDone = i < stepIdx || (i !== stepIdx && !!selections[s.id]?.productId);
              return (
              <div key={s.id} className="prog-item">
                {i > 0 && <div className="prog-line" />}
                <div className={`prog-pill${isDone ? ' prog-done' : i === stepIdx ? ' prog-active' : ''}`}>
                  <span className="pn">
                    {isDone ? (
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M1.5 5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : i + 1}
                  </span>
                  <span className="pl">{s.label}</span>
                </div>
              </div>
              );
            })}
          </div>
        )}

        <div className="sheet-div" />

        {/* Scrollable body */}
        <div className="deal-sheet-body" ref={bodyRef}>
          {loading ? (
            <div className="deal-loading-center"><div className="deal-spinner" /></div>
          ) : dealData ? (
            <>
              {/* Step headline */}
              {currentStep && (
                <div className="step-hd">
                  <div className="step-eyebrow">{currentStep.eyebrow}</div>
                  <div className="step-title">{currentStep.title}</div>
                </div>
              )}

              {/* Tier tabs */}
              {currentStep?.tiers?.length > 1 && (
                <>
                  <div className="tier-upgrade-lbl">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                      <path d="M6 1l1.4 2.8 3.1.45-2.25 2.19.53 3.09L6 8.1 3.22 9.53l.53-3.09L1.5 4.25l3.1-.45L6 1z"/>
                    </svg>
                    Choose your menu
                  </div>
                  <div className="tier-tabs">
                    {currentStep.tiers.map(tier => (
                      <button
                        key={tier.id}
                        className={`tier-tab${getActiveTierId(currentStep) === tier.id ? ' on' : ''}`}
                        onClick={() => {
                          setActiveTier(prev => ({ ...prev, [currentStep.id]: tier.id }));
                          setStepSubCat(prev => ({ ...prev, [currentStep.id]: '' }));
                        }}
                      >
                        <span className="tt-name">{tier.label}</span>
                        {tier.upgrade > 0
                          ? <span className="tt-tag">+${tier.upgrade}</span>
                          : <span className="tt-tag free">Included</span>
                        }
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Sub-category filter pills — built from dishCatName of size-filtered products */}
              {currentStep && (() => {
                const sizeFiltered = getStepProducts(currentStep);
                const subCatMap = {};
                sizeFiltered.forEach(p => {
                  if (p.dishCatName) subCatMap[p.dishCatName] = (subCatMap[p.dishCatName] || 0) + 1;
                });
                const subCatNames = Object.keys(subCatMap);
                if (subCatNames.length < 2) return null;
                const activeCat = stepSubCat[currentStep.id] || '';
                return (
                  <div className="subcat-filter-row">
                    <button
                      className={`subcat-pill${!activeCat ? ' on' : ''}`}
                      onClick={() => setStepSubCat(prev => ({ ...prev, [currentStep.id]: '' }))}
                    >
                      All <span className="scp-count">{sizeFiltered.length}</span>
                    </button>
                    {subCatNames.map(cat => (
                      <button
                        key={cat}
                        className={`subcat-pill${activeCat === cat ? ' on' : ''}`}
                        onClick={() => setStepSubCat(prev => ({ ...prev, [currentStep.id]: cat }))}
                      >
                        {cat} <span className="scp-count">{subCatMap[cat]}</span>
                      </button>
                    ))}
                  </div>
                );
              })()}

              {/* Product grid */}
              {currentStep && (() => {
                const activeCat    = stepSubCat[currentStep.id] || '';
                const sizeFiltered = getStepProducts(currentStep);
                const displayProds = activeCat
                  ? sizeFiltered.filter(p => p.dishCatName === activeCat)
                  : sizeFiltered;
                const activeTierId  = getActiveTierId(currentStep);
                const activeTierObj = currentStep.tiers?.find(t => t.id === activeTierId);
                const stepSizeIds   = (currentStep.sizes || []).map(s => s.id);
                const tierSubCats   = activeTierObj?.subCats || [];
                // Per-product upgrade cost: tier.subCats restricts which products incur the price.
                // If subCats is empty, all products cost tier.upgrade. If non-empty, only products
                // whose dishcat is in the list cost tier.upgrade; others are included (matching pos_web).
                const prodUpgradeCost = (prod) => {
                  const base = activeTierObj?.upgrade || 0;
                  if (!base) return 0;
                  if (tierSubCats.length > 0 && !tierSubCats.includes(prod.dishcat)) return 0;
                  return base;
                };
                return (
                  <div className="prod-grid">
                    {displayProds.length === 0 ? (
                      <div className="prod-empty">No items available for this step.</div>
                    ) : (
                      displayProds.map(prod => {
                        const isSelected = sel?.productId === prod.id;
                        // Sizes to show on card: intersection of step sizes and prod.sizes
                        const cardSizes = stepSizeIds.length > 0
                          ? currentStep.sizes.filter(s => prod.sizes == null || prod.sizes.includes(s.id))
                          : [];
                        return (
                          <div
                            key={prod.id}
                            className={`prod-card${isSelected ? ' selected' : ''}${prod.customisable ? ' customisable' : ''}`}
                            onClick={() => handleProductClick(currentStep, prod)}
                          >
                            <div className="prod-check">
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            {prod.img
                              ? <img className="prod-img" src={prod.img} alt={prod.name} />
                              : <div className="prod-img-placeholder">🍽</div>
                            }
                            <div className="prod-body">
                              <div className="prod-name">{prod.name}</div>
                              {cardSizes.length > 0 && (
                                <div className="prod-sizes">
                                  {cardSizes.map(s => (
                                    <span key={s.id} className="prod-size-pill">{s.label}</span>
                                  ))}
                                </div>
                              )}
                              <div className={`prod-price-tag${!prodUpgradeCost(prod) ? ' incl' : ''}`}>
                                {prodUpgradeCost(prod) ? `+$${prodUpgradeCost(prod).toFixed(2)}` : 'Included'}
                              </div>
                              {prod.customisable && isSelected && (
                                <div className="prod-customise-row">
                                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                    <circle cx="5" cy="5" r="4" stroke="currentColor" strokeWidth="1.4"/>
                                    <path d="M5 3v2l1.2 1.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                                  </svg>
                                  Customise
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                );
              })()}

              {/* Size picker (non-customisable products, or customisable with empty recipe) */}
              {currentStep?.sizes?.length > 0 && sel?.productId && !cpOpen && (
                (() => {
                  const prod = getStepProducts(currentStep).find(p => p.id === sel.productId);
                  if (!prod) return null; // selected product not visible in current tab — hide size row
                  if (prod?.customisable) {
                    // If recipe hasn't loaded yet, or it has content, sizes are in cp-wrap
                    const rec = recipes[prod.id];
                    if (rec === undefined) return null; // still loading
                    if (!recipeIsEmpty(rec)) return null; // has content
                    // Recipe loaded and empty — show sizes inline
                  }
                  return (
                    <div className="size-row" style={{ padding: '12px 0 0' }}>
                      {stepSizesForProduct(currentStep.sizes, prod).map(size => (
                        <button
                          key={size.id}
                          className={`size-pill${sel?.sizeId === size.id ? ' on' : ''}`}
                          onClick={() => handleSizeClick(currentStep.id, size.id)}
                        >
                          <span className="sn">{size.label}</span>
                          {size.price > 0
                            ? <span className="sp up">+${size.price}</span>
                            : <span className="sp">Included</span>
                          }
                        </button>
                      ))}
                    </div>
                  );
                })()
              )}

            </>
          ) : (
            <div className="deal-loading-center" style={{ color: 'var(--muted)', fontSize: 13 }}>No deal data found.</div>
          )}
        </div>

        {/* Customise sub-panel — sibling of body so position:absolute inset:0 covers full modal */}
        {renderCpPanel()}

        {/* Footer */}
        {!cpOpen && (
          <div className="sheet-ft">
            <button
              className={`ft-back${stepIdx === 0 ? ' hidden' : ''}`}
              onClick={handleBack}
              disabled={submitting}
              aria-label="Back"
            >
              <svg width="9" height="14" viewBox="0 0 9 14" fill="none">
                <path d="M7.5 1L1.5 7l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <button className={nextCls()} onClick={handleNext} disabled={submitting || loading}>
              {submitting ? (
                <span className="ft-spinner-btn" />
              ) : (
                <>
                  <span>{footerLabel()}</span>
                  {canAdvance && (
                    <span className="ft-arrow-circle">
                      <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                        <path d="M3.5 8h9M9 5l3 3-3 3" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  )}
                </>
              )}
            </button>
          </div>
        )}

      </div>
    </CommonModal>
  );
};

/* ── Accordion sub-component ──────────────────────────────────────────── */
const CpAccordion = ({ title, summary, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`acc${open ? ' open' : ''}`}>
      <button className="acc-hd" onClick={() => setOpen(o => !o)}>
        <span className="acc-title">{title}</span>
        <span className="acc-summary">{summary}</span>
        <svg className="acc-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <div className="acc-body">
        <div className="acc-inner">{children}</div>
      </div>
    </div>
  );
};

export default DealPopupModal;
